import React, { createContext, ReactNode, useContext, useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { matchConsolidationDetailRoutePath } from '@futrli/shared-services/organisations/utils/routes/consolidations';
import { ModifyConsolidatedForecastView } from '@futrli/views/views/forecasts/ModifyConsolidatedForecastView/ModifyConsolidatedForecastView';
import { ModifyForecastView } from '@futrli/views/views/forecasts/ModifyForecastView/ModifyForecastView';
import { useApiView } from '@futrli/views/hooks/useApiView';

import { useUpdateForecastForm } from '../hooks/useUpdateForecastForm';

interface UpdateForecastContextProps {
  children?: ReactNode;
  forecastId: string;
}

interface UpdateForecastContextValues {
  form: ReturnType<typeof useUpdateForecastForm>;
  view: ModifyForecastView | ModifyConsolidatedForecastView;
}

const defaultState = ({} as unknown) as UpdateForecastContextValues;

export const UpdateForecastContext = createContext(defaultState);

export const UpdateForecastContextProvider = ({ children, forecastId }: UpdateForecastContextProps) => {
  const { workspaceId = '', organisationId = '' } = useParams();
  const isConsolidation = !!matchConsolidationDetailRoutePath(window.location.pathname);

  const ViewClass = isConsolidation ? ModifyConsolidatedForecastView : ModifyForecastView;
  const view = useApiView(
    useMemo(() => new ViewClass(workspaceId, organisationId, forecastId), [
      ViewClass,
      workspaceId,
      organisationId,
      forecastId
    ])
  );

  const form = useUpdateForecastForm(view.state.forecast);

  const values = {
    form,
    view
  };

  return <UpdateForecastContext.Provider value={values}>{children}</UpdateForecastContext.Provider>;
};

export const useUpdateForecastContext = () => useContext(UpdateForecastContext);
